import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useCustomer, usePerspective } from "../../contexts/app/provider";
import { ChatContext } from "../../contexts/chat/context";
import { LayoutContext } from "../../contexts/layout/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import type { DatePivotParams } from "../../entities/date_pivot_params";
import {
  createCommonViewContext,
  type CommonViewContextV1,
} from "../../entities/common_view_context";
import {
  createSupplyWalkCellContext,
  isSupportedSupplyWalkCellContext,
  supplyWalkCellPrompt,
  type CommonSupplyWalkCellContextV1,
  type SupplyWalkVariant,
} from "../../entities/supply_walk_cell_context";
import {
  awaitNucleusExplainStart,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_STALE_REASON,
  rejectNucleusExplainActivation,
  type NucleusExplainActivation,
} from "../../entities/nucleus_explain_action";
import { useGetTransformedRawParquetSubscription } from "../../services/perspective_api/__generated__/perspective_server-types";
import type { Run } from "../../services/appsync/types";
import { useSynchronizedPending } from "../../components/nucleus_explain/use_synchronized_pending";

const RECEIPT_ID = /^rvr1_[a-f0-9]{64}$/;
const STANDARD_CUSTOMERS = new Set([
  "starface_world",
  "goodr",
  "vincero",
  "oofos",
]);
const HUB_CUSTOMERS = new Set(["cora"]);

export type SupplyWalkExplainCandidate = Readonly<{
  measure: unknown;
  measureRank: unknown;
  periodStartEpochMs: unknown;
  dimensions: Readonly<Record<string, unknown>>;
  rawValue: unknown;
}>;

export type SupplyWalkCellExplanationController = Readonly<{
  canExplain: boolean;
  cellIdentityPrefix: string;
  isBusy: boolean;
  explainCell: (
    candidate: SupplyWalkExplainCandidate,
  ) => NucleusExplainActivation;
}>;

type HookInput = Readonly<{
  variant: SupplyWalkVariant;
  viewParams: DatePivotParams | null;
  editMode: boolean;
  dirty: boolean;
  interactionIdentity: string;
}>;

type PendingCell = Readonly<{
  prompt: string;
  context: CommonSupplyWalkCellContextV1;
  viewContext: CommonViewContextV1;
  runIdentity: string;
  sourceIdentity: string;
  interactionIdentity: string;
  viewIdentity: string;
  connectionReady: boolean;
}>;

const runIdentity = (run: Run | null | undefined): string | undefined =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : undefined;

const customerSupportsVariant = (
  customerId: string,
  variant: SupplyWalkVariant,
): boolean =>
  variant === "for_hubs"
    ? HUB_CUSTOMERS.has(customerId)
    : STANDARD_CUSTOMERS.has(customerId);

const exactViewRecipe = (
  viewParams: DatePivotParams | null,
): viewParams is DatePivotParams & {
  minDate: Date;
  maxDate: Date;
  segments: string[];
} =>
  Boolean(
    viewParams &&
      viewParams.dateHierarchy === "week" &&
      viewParams.minDate instanceof Date &&
      Number.isFinite(viewParams.minDate.getTime()) &&
      viewParams.maxDate instanceof Date &&
      Number.isFinite(viewParams.maxDate.getTime()) &&
      viewParams.minDate <= viewParams.maxDate &&
      Array.isArray(viewParams.segments) &&
      viewParams.segments.length === 2 &&
      viewParams.segments[0] === "sku" &&
      viewParams.segments[1] === "location",
  );

export const useSupplyWalkCellExplanation = ({
  variant,
  viewParams,
  editMode,
  dirty,
  interactionIdentity,
}: HookInput): SupplyWalkCellExplanationController => {
  const { selectedCustomerId } = useCustomer();
  const { perspectiveApiService } = usePerspective();
  const {
    mainRun,
    lastSuccessfulRun,
    comparedRun = null,
  } = useContext(VersionedDataContext);
  const { isComparing, setIsSidebarOpen } = useContext(LayoutContext);
  const {
    connectChat,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    sendMessage,
    transportKind,
  } = useContext(ChatContext);
  const activeRun = mainRun ?? lastSuccessfulRun;
  const activeRunIdentity = runIdentity(activeRun);
  const hasExactView = exactViewRecipe(viewParams);
  const supportsRoute = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      !isComparing &&
      comparedRun === null &&
      hasExactView &&
      customerSupportsVariant(activeRun.runSettings.organizationId, variant) &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId),
  );

  const params = useMemo(
    () =>
      hasExactView
        ? {
            min_date: viewParams.minDate.toISOString(),
            max_date: viewParams.maxDate.toISOString(),
            date_field: "week",
            measure: "Ending Inventory On Hand",
            value_field: "Ending Inventory On Hand",
            segmentation_fields: ["sku", "location"],
            filters: [],
            post_filters: [],
          }
        : {
            min_date: null,
            max_date: null,
            date_field: "week",
            measure: "Ending Inventory On Hand",
            value_field: "Ending Inventory On Hand",
            segmentation_fields: ["sku", "location"],
            filters: [],
            post_filters: [],
          },
    [hasExactView, viewParams],
  );
  const variables = useMemo(
    () => ({
      datasetName: "supply_walk_test",
      transformId:
        variant === "for_hubs"
          ? "supply_walk_parallelized_matrix_for_hubs"
          : "supply_walk_parallelized_matrix",
      folder: "outputs",
      runSettingsJson: activeRun?.runSettings ?? null,
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params,
    }),
    [activeRun?.runSettings, params, variant],
  );
  const requestKey = useMemo(
    () => JSON.stringify({ selectedCustomerId, variables }),
    [selectedCustomerId, variables],
  );
  const currentRequestKeyRef = useRef(requestKey);
  currentRequestKeyRef.current = requestKey;
  const [receiptSource, setReceiptSource] = useState<
    | Readonly<{
        receiptId: string;
        run: Run;
        identity: string;
        requestKey: string;
      }>
    | undefined
  >(undefined);
  const {
    pending: pendingCell,
    pendingRef: pendingCellRef,
    setPending: setPendingCell,
    clearPending,
    replacePending,
  } = useSynchronizedPending<PendingCell>();

  const invalidateReceiptSource = useCallback(() => {
    setReceiptSource(undefined);
    const pending = pendingCellRef.current;
    if (pending) clearPending(pending);
  }, [clearPending, pendingCellRef]);

  useEffect(() => {
    invalidateReceiptSource();
  }, [invalidateReceiptSource, requestKey]);

  useGetTransformedRawParquetSubscription({
    client: perspectiveApiService.getClient(),
    variables,
    skip: !supportsRoute,
    onData: (event: any) => {
      if (currentRequestKeyRef.current !== requestKey || !supportsRoute) return;
      const payload = event.data?.data?.getTransformedRawParquet;
      const receiptId = payload?.renderedViewReceiptId;
      const hasError =
        typeof payload?.errorMessage === "string"
          ? payload.errorMessage.length > 0
          : payload?.errorMessage != null;
      if (hasError) {
        invalidateReceiptSource();
        return;
      }
      if (payload?.status === "LOADING") return;
      if (payload?.status !== "COMPLETED") {
        invalidateReceiptSource();
        return;
      }
      if (
        typeof payload.parquetData !== "string" ||
        payload.parquetData.length === 0 ||
        typeof receiptId !== "string" ||
        !RECEIPT_ID.test(receiptId) ||
        !activeRun
      ) {
        invalidateReceiptSource();
        return;
      }
      const identity = JSON.stringify([
        requestKey,
        String(payload?.id ?? "completed"),
        receiptId,
      ]);
      const nextReceiptSource = Object.freeze({
        receiptId,
        run: activeRun,
        identity,
        requestKey,
      });
      setReceiptSource((current) =>
        current?.identity === identity ? current : nextReceiptSource,
      );
      const pending = pendingCellRef.current;
      if (pending && pending.sourceIdentity !== identity) {
        clearPending(pending);
      }
    },
  });

  const currentViewContext = useMemo(
    () =>
      viewParams
        ? createCommonViewContext({
            surfaceId: "supply-walk",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          })
        : undefined,
    [viewParams],
  );
  const currentViewIdentity = useMemo(
    () => (currentViewContext ? JSON.stringify(currentViewContext) : "no-view"),
    [currentViewContext],
  );
  const receiptSourceForRequest =
    receiptSource?.requestKey === requestKey ? receiptSource : undefined;
  const canExplain = Boolean(
    supportsRoute &&
      receiptSourceForRequest &&
      currentViewContext &&
      !editMode &&
      !dirty,
  );
  const readinessContextRef = useRef({
    activeRunIdentity,
    canExplain,
    dirty,
    editMode,
    interactionIdentity,
    receiptSourceIdentity: receiptSourceForRequest?.identity,
    viewIdentity: currentViewIdentity,
  });
  readinessContextRef.current = {
    activeRunIdentity,
    canExplain,
    dirty,
    editMode,
    interactionIdentity,
    receiptSourceIdentity: receiptSourceForRequest?.identity,
    viewIdentity: currentViewIdentity,
  };
  const isBusy = isAwaitingResponse || Boolean(pendingCell);
  const cellIdentityPrefix = useMemo(
    () =>
      JSON.stringify([
        variant,
        activeRunIdentity ?? "no-run",
        receiptSourceForRequest?.identity ?? "no-source",
        currentViewIdentity,
        interactionIdentity,
      ]),
    [
      activeRunIdentity,
      currentViewIdentity,
      interactionIdentity,
      receiptSourceForRequest?.identity,
      variant,
    ],
  );

  const explainCell = useCallback(
    (candidate: SupplyWalkExplainCandidate) => {
      if (
        !canExplain ||
        !receiptSourceForRequest ||
        currentRequestKeyRef.current !== requestKey ||
        !currentViewContext ||
        !activeRun ||
        !activeRunIdentity ||
        runIdentity(receiptSourceForRequest.run) !== activeRunIdentity
      ) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      if (isAwaitingResponse || pendingCellRef.current) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_BUSY_REASON);
      }
      const context = createSupplyWalkCellContext({
        variant,
        receiptId: receiptSourceForRequest.receiptId,
        measure: candidate.measure,
        measureRank: candidate.measureRank,
        periodField: "week",
        periodStartEpochMs: candidate.periodStartEpochMs,
        segments: ["sku", "location"],
        dimensions: candidate.dimensions,
        rawValue: candidate.rawValue,
        editMode,
        dirty,
        comparison: false,
      });
      if (!context || !isSupportedSupplyWalkCellContext(context)) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      const nextPending = Object.freeze({
        prompt: supplyWalkCellPrompt(context),
        context,
        viewContext: currentViewContext,
        runIdentity: activeRunIdentity,
        sourceIdentity: receiptSourceForRequest.identity,
        interactionIdentity,
        viewIdentity: currentViewIdentity,
        connectionReady: false,
      });
      setPendingCell(nextPending);
      setIsSidebarOpen(true);
      return awaitNucleusExplainStart(
        () => connectChat(activeRun, "supply-walk"),
        () => clearPending(nextPending),
        () => {
          const latest = readinessContextRef.current;
          if (
            currentRequestKeyRef.current !== requestKey ||
            !latest.canExplain ||
            latest.editMode ||
            latest.dirty ||
            latest.activeRunIdentity !== nextPending.runIdentity ||
            latest.receiptSourceIdentity !== nextPending.sourceIdentity ||
            latest.interactionIdentity !== nextPending.interactionIdentity ||
            latest.viewIdentity !== nextPending.viewIdentity
          ) {
            return false;
          }
          return replacePending(
            nextPending,
            Object.freeze({ ...nextPending, connectionReady: true }),
          );
        },
      );
    },
    [
      activeRun,
      activeRunIdentity,
      canExplain,
      clearPending,
      connectChat,
      currentViewContext,
      currentViewIdentity,
      dirty,
      editMode,
      interactionIdentity,
      isAwaitingResponse,
      pendingCellRef,
      receiptSourceForRequest,
      replacePending,
      requestKey,
      setPendingCell,
      setIsSidebarOpen,
      variant,
    ],
  );

  useEffect(() => {
    if (!pendingCell) return;
    if (
      !supportsRoute ||
      editMode ||
      dirty ||
      pendingCell.runIdentity !== activeRunIdentity ||
      pendingCell.sourceIdentity !== receiptSourceForRequest?.identity ||
      pendingCell.interactionIdentity !== interactionIdentity ||
      pendingCell.viewIdentity !== currentViewIdentity
    ) {
      clearPending(pendingCell);
      return;
    }
    if (
      !pendingCell.connectionReady ||
      !isConnected ||
      isConnecting ||
      isAwaitingResponse
    ) {
      return;
    }
    sendMessage(
      pendingCell.prompt,
      pendingCell.context,
      pendingCell.viewContext,
    );
    clearPending(pendingCell);
  }, [
    activeRunIdentity,
    clearPending,
    dirty,
    editMode,
    interactionIdentity,
    currentViewIdentity,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    pendingCell,
    receiptSourceForRequest?.identity,
    sendMessage,
    supportsRoute,
  ]);

  return useMemo(
    () =>
      Object.freeze({
        canExplain,
        cellIdentityPrefix,
        isBusy,
        explainCell,
      }),
    [canExplain, cellIdentityPrefix, explainCell, isBusy],
  );
};
